import type { ArtefactMeta, Verification } from "@/lib/artefact";

type Props = {
  meta: ArtefactMeta;
};

const VERIFICATION_LABEL: Record<Verification, string> = {
  "primary-verified": "primary-verified",
  "press-quote": "press quote",
  "automated-extraction": "auto-extracted",
  "munidata-pipeline": "machine-ingested",
  "derived-allocation": "modelled estimate",
  unverified: "unverified",
};

// Translate the internal tier identifier to the kebab-case slug used
// in the rendered `data-state` attribute. The `munidata-pipeline` enum
// value MUST NOT reach DOM — the chip exposes "machine-ingested" so a
// reader inspecting the rendered HTML never sees the upstream pipeline
// name (per FR-019, SC-005, and Constitution v1.3.0 Principle VII).
// Other tiers pass through as-is; they don't carry a leak risk.
const VERIFICATION_DATA_STATE: Record<Verification, string> = {
  "primary-verified": "primary-verified",
  "press-quote": "press-quote",
  "automated-extraction": "automated-extraction",
  "munidata-pipeline": "machine-ingested",
  "derived-allocation": "derived-allocation",
  unverified: "unverified",
};

const VERIFICATION_DESCRIPTION: Record<Verification, string> = {
  "primary-verified":
    "Every figure on this chart was confirmed verbatim against the cited primary source.",
  "press-quote":
    "Figures here come from a press paraphrase of a primary speaker; no primary transcript is publicly available. Treat with appropriate caution.",
  "automated-extraction":
    "Figures here were produced by an automated extraction pipeline (Gemini PDF→JSON) from the cited source. The extraction is reproducible from the source hash but has not been independently re-verified by a human.",
  "munidata-pipeline":
    "Structural data was machine-ingested at build time from the cited primary source. The source URL was fetched and hashed, but its contents have not yet been opened by a human reader and cross-checked against the artefact. Treat individual figures with appropriate caution; a hand-verified pass will follow.",
  "derived-allocation":
    "Figures here are a modelled estimate, not an official measurement. A documented formula allocates citywide totals across smaller units using cited driver datasets. Every input source meets the primary-verified standard, and the allocation is reproducible from this artefact alone — see the methodology page for the exact transformation, its limitations, and how to re-derive any figure.",
  unverified:
    "This artefact has not been verified against a primary source and should not be on the site.",
};

export function SourceCite({ meta }: Props) {
  const verification = meta.verification ?? "unverified";
  return (
    <figcaption className="mt-5 border-t border-rule pt-4 grid grid-cols-12 gap-x-4 gap-y-2 text-sm [overflow-wrap:anywhere]">
      <div className="col-span-12 sm:col-span-2 cs-label">verification</div>
      <div className="col-span-12 sm:col-span-10 min-w-0">
        <span
          className="cs-verify-chip"
          data-state={VERIFICATION_DATA_STATE[verification]}
          title={VERIFICATION_DESCRIPTION[verification]}
        >
          {VERIFICATION_LABEL[verification]}
        </span>
      </div>

      <div className="col-span-12 sm:col-span-2 cs-label">derivation</div>
      <div className="col-span-12 sm:col-span-10 min-w-0 text-ink-2 italic leading-snug">
        {typeof meta.derivation === "string"
          ? meta.derivation
          : meta.derivation.summary}
      </div>

      <div className="col-span-12 sm:col-span-2 cs-label">
        {meta.sources.length > 1 ? "sources" : "source"}
      </div>
      <ul className="col-span-12 sm:col-span-10 min-w-0 flex flex-wrap gap-1.5 list-none p-0">
        {meta.sources.map((s, i) => {
          const isPointer = s.verified_by === "context-pointer";
          return (
            <li key={`${i}-${s.url}`}>
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="cs-source-chip group"
                data-verified={
                  isPointer ? "pointer" : s.verified_at ? "yes" : "no"
                }
                title={`${s.title} — retrieved ${s.retrieved_at}${
                  s.verified_at && !isPointer
                    ? ` · verified ${s.verified_at}`
                    : ""
                }`}
              >
                <span>{s.publisher}</span>
                <span aria-hidden className="text-ink-3 group-hover:text-ink-2">·</span>
                <span className="text-ink-3 group-hover:text-ink-2 normal-case">
                  ret {s.retrieved_at}
                </span>
                {s.verified_at && !isPointer && (
                  <>
                    <span aria-hidden className="text-ink-3 group-hover:text-ink-2">·</span>
                    <span className="text-ink-3 group-hover:text-ink-2 normal-case italic">
                      ver {s.verified_at}
                    </span>
                  </>
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </figcaption>
  );
}
